/**
 * Dynamic scroll height for the vertical collection facets sidebar.
 *
 * The CSS `max-height` on `.collection-facets__scroller` is sized for the state
 * where the sidebar is stuck to the top of the viewport. Until the page is
 * scrolled down to that point the sidebar sits lower, so that fixed height lets
 * the filter list run off the bottom of the screen without ever becoming
 * scrollable. This keeps `max-height` equal to the space actually visible below
 * the sidebar's current top edge, so the list always fits the viewport and gets
 * its own scrollbar as soon as it is taller than what is on screen.
 *
 * After every event it recomputes each frame for a short settle window, so it
 * rides out the CSS transition on `.sticky-element` instead of sampling a
 * mid-animation position once and going stale.
 */
(() => {
  const BREAKPOINT = "(min-width: 1280px)";
  const BOTTOM_GAP = 16; // px of breathing room below the sidebar
  const MIN_HEIGHT = 200; // fallback height while the sidebar sits below the fold
  const SETTLE_MS = 600; // recompute this long after events to ride out CSS transitions

  const init = () => {
    const container = document.getElementById("FacetFiltersContainer");
    const scroller =
      container && container.querySelector(".collection-facets__scroller");
    if (!scroller) return;

    const mq = window.matchMedia(BREAKPOINT);
    let frame = null;
    let settleUntil = 0;
    let lastValue = "";

    const apply = () => {
      let value = "";
      // Below the breakpoint the sidebar is display:none; let the CSS handle it.
      if (mq.matches && scroller.offsetParent !== null) {
        const { top } = scroller.getBoundingClientRect();
        const available = Math.round(window.innerHeight - top - BOTTOM_GAP);
        // Use the visible space exactly so the panel never overshoots the viewport.
        value = `${available > 0 ? available : MIN_HEIGHT}px`;
      }
      if (value !== lastValue) {
        scroller.style.maxHeight = value;
        lastValue = value;
      }
      // Keep going for a short while so CSS transitions and late reflows settle.
      frame =
        performance.now() < settleUntil
          ? window.requestAnimationFrame(apply)
          : null;
    };

    const schedule = () => {
      settleUntil = performance.now() + SETTLE_MS;
      if (frame === null) frame = window.requestAnimationFrame(apply);
    };

    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("load", schedule);

    if (mq.addEventListener) {
      mq.addEventListener("change", schedule);
    } else if (mq.addListener) {
      mq.addListener(schedule);
    }

    // Layout changes that do not fire scroll/resize still need a recompute.
    if (window.ResizeObserver) {
      new ResizeObserver(schedule).observe(document.body);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
